import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LocaleService } from '../locale-service/locale.service';
import { SecureStorageKey } from '../storage/models/secure-storage-key.enum';
import { SecureStorageService } from '../storage/secure-storage.service';
import {
  kilogramsToStonePounds,
  StonePoundsWeight,
  stonePoundsToKilograms,
} from './weight-conversion.util';

@Injectable({
  providedIn: 'root',
})
export class WeightDisplayService {
  private useStonePreference = false;
  private initialization?: Promise<void>;

  constructor(
    private localeService: LocaleService,
    private translate: TranslateService,
  ) {}

  initialize(): Promise<void> {
    if (!this.initialization) {
      this.initialization = this.loadPreference();
    }
    return this.initialization;
  }

  isGBCulture(): boolean {
    return this.localeService.isGBCulture();
  }

  usesStone(): boolean {
    return this.isGBCulture() && this.useStonePreference;
  }

  /**
   * Imperial pounds are for non-GB cultures only. en-GB weight is kg or stone.
   */
  shouldDisplayImperialPounds(useImperial: boolean): boolean {
    return !!useImperial && !this.isGBCulture();
  }

  getStonePreference(): boolean {
    return this.useStonePreference;
  }

  async setStonePreference(useStone: boolean): Promise<void> {
    this.useStonePreference = useStone;
    await SecureStorageService.set(
      SecureStorageKey.UseStoneWeightDisplay,
      String(useStone),
    );
  }

  /** Clear in-memory preference after logout so the next account starts from storage. */
  reset(): void {
    this.useStonePreference = false;
    this.initialization = undefined;
  }

  toStonePounds(kilograms: number): StonePoundsWeight {
    return kilogramsToStonePounds(kilograms);
  }

  toKilograms(stones: number, pounds: number): number {
    return stonePoundsToKilograms(stones, pounds);
  }

  formatKilogramsAsStone(kilograms: number): string {
    const weight = this.toStonePounds(kilograms);
    return this.translate.instant('weight-display-service.stone-pounds', weight);
  }

  private async loadPreference(): Promise<void> {
    const stored = await SecureStorageService.get(SecureStorageKey.UseStoneWeightDisplay);
    this.useStonePreference = stored === 'true';
  }
}
